import { forwardRef, useLayoutEffect, useMemo, useRef, useState, type ComponentProps } from "react";
import AutoGrowTextarea from "@/shared/components/AutoGrowTextarea";
import type { AdToken } from "@/shared/adTokens/adTokens";
import { scanAdTokens } from "@/shared/adTokens/adTokenScan";
import AdTokenHighlight from "@/shared/adTokens/AdTokenHighlight";
import AdTokenMenu from "@/shared/adTokens/AdTokenMenu";
import { useAdTokenPicker, type AdTokenFieldHandle } from "@/shared/adTokens/useAdTokenPicker";

/**
 * An ad copy field that offers the token vocabulary when the writer types `[` (MPN-1932).
 * What the picker does is useAdTokenPicker; this is the textarea it does it in, with the
 * menu hung off the caret.
 */

type AdTokenTextareaProps = Omit<ComponentProps<typeof AutoGrowTextarea>, "onChange" | "ref"> & {
    /** The vocabulary, already loaded. Empty means the picker never opens. */
    tokens: AdToken[];
    /** The text after an edit, whether it was typed or inserted from the menu. */
    onValueChange: (value: string) => void;
};

/** Edits made to the field from outside it -- see AdTokenFieldHandle. */
export type AdTokenTextareaHandle = AdTokenFieldHandle;

/** Where the caret sits on screen, and how tall one line is, so the menu can clear it. */
type CaretPoint = { top: number; left: number; lineHeight: number };

/**
 * Measure the caret by rendering the text into a hidden div laid out exactly like the
 * textarea and asking the browser where the character after the caret landed.
 *
 * A textarea gives no way to ask this directly. The mirror sits at the textarea's top-left
 * corner and copies the properties that decide where text wraps, so the span's offset inside
 * it is the caret's offset inside the textarea.
 */
function measureCaret(
    textarea: HTMLTextAreaElement,
    mirror: HTMLDivElement,
    index: number,
): CaretPoint {
    const computed = window.getComputedStyle(textarea);
    const style = mirror.style;
    style.width = `${textarea.clientWidth}px`;
    style.boxSizing = "border-box";
    style.font = computed.font;
    style.fontFamily = computed.fontFamily;
    style.fontSize = computed.fontSize;
    style.lineHeight = computed.lineHeight;
    style.letterSpacing = computed.letterSpacing;
    style.padding = computed.padding;

    mirror.textContent = textarea.value.slice(0, index);
    const marker = document.createElement("span");
    // A trailing caret has no character after it; any glyph measures the same position.
    marker.textContent = textarea.value.slice(index, index + 1) || ".";
    mirror.appendChild(marker);
    const top = marker.offsetTop;
    const left = marker.offsetLeft;
    mirror.textContent = "";

    const box = textarea.getBoundingClientRect();
    return {
        top: box.top + top - textarea.scrollTop,
        left: box.left + left - textarea.scrollLeft,
        lineHeight: parseFloat(computed.lineHeight) || 20,
    };
}

const AdTokenTextarea = forwardRef<AdTokenTextareaHandle, AdTokenTextareaProps>(function AdTokenTextarea({
    tokens,
    value,
    onValueChange,
    onKeyDown,
    onBlur,
    onClick,
    onKeyUp,
    onScroll,
    onCompositionStart,
    onCompositionEnd,
    style,
    className,
    ...rest
}, ref) {
    const picker = useAdTokenPicker<HTMLTextAreaElement>({ tokens, value, onValueChange, ref });
    const { context, open } = picker;
    const mirrorRef = useRef<HTMLDivElement>(null);
    const [caret, setCaret] = useState<CaretPoint | null>(null);

    // The tokens to color (MPN-1930). Not debounced, unlike the notes: a highlight has to sit
    // on the text as it is, and a bracket only becomes a token once its `]` is typed, so
    // nothing half-typed flashes. With no vocabulary there is nothing to color.
    const ranges = useMemo(() => (tokens.length ? scanAdTokens(value, tokens) : []), [value, tokens]);

    // Measured after render, not in the change handler: the token menu hangs off the caret,
    // and the box the caret sits in has just resized itself to the new text.
    useLayoutEffect(() => {
        if (!context) return;
        const textarea = picker.fieldRef.current;
        const mirror = mirrorRef.current;
        if (!textarea || !mirror) return;
        setCaret(measureCaret(textarea, mirror, context.start));
    }, [context, value, picker.fieldRef]);

    const anchorEl = useMemo(() => {
        if (!caret) return null;
        return {
            getBoundingClientRect: () =>
                ({
                    top: caret.top,
                    left: caret.left,
                    right: caret.left,
                    bottom: caret.top + caret.lineHeight,
                    width: 0,
                    height: caret.lineHeight,
                    x: caret.left,
                    y: caret.top,
                    toJSON: () => "",
                }) as DOMRect,
        };
    }, [caret]);

    return (
        <div style={{ position: "relative" }}>
            {/*
                The measuring mirror. Hidden and out of the flow, and sitting exactly where the
                textarea does, so a character's offset inside it is that character's offset
                inside the textarea.
            */}
            <div
                ref={mirrorRef}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    visibility: "hidden",
                    pointerEvents: "none",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    zIndex: -1,
                }}
            />
            {/* The tokens colored in place (MPN-1930). Even a single-line field wraps, so the
                backdrop wraps too. */}
            <AdTokenHighlight value={value} ranges={ranges} multiline className={className} style={style}>
                {(field) => (
                    <AutoGrowTextarea
                        {...rest}
                        ref={(node) => {
                            picker.fieldRef.current = node;
                            field.ref(node);
                        }}
                        value={value}
                        className={field.className}
                        style={field.style}
                        onScroll={(e) => {
                            onScroll?.(e);
                            field.onScroll(e);
                        }}
                        onCompositionStart={(e) => {
                            onCompositionStart?.(e);
                            field.onCompositionStart(e);
                        }}
                        onCompositionEnd={(e) => {
                            onCompositionEnd?.(e);
                            field.onCompositionEnd(e);
                        }}
                        {...picker.fieldProps({ onKeyDown, onKeyUp, onClick, onBlur })}
                    />
                )}
            </AdTokenHighlight>
            {open && anchorEl && (
                <AdTokenMenu
                    anchorEl={anchorEl}
                    value={value}
                    context={context}
                    matches={picker.matches}
                    active={picker.active}
                    onActive={picker.setActive}
                    onInsert={picker.insert}
                />
            )}
        </div>
    );
});

export default AdTokenTextarea;
